import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, Plus, Sparkles, Trash2, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { apiService } from '@/services/api'
import type { GameAbility } from '@/types'

// Instância de habilidade (resposta enriquecida do backend) — espelho do
// InventoryEntry do ItemsPanel, sem quantity (habilidade não empilha).
export interface AbilityEntry {
  id?: string
  abilityId: string
  name?: string
  description?: string
  icon?: string
  removed?: boolean
}

// Personagem (subset usado aqui — camelCase como na GameSession)
export interface PanelCharacter {
  id: string
  name: string
  userId: string
  isNpc?: boolean
}

interface AbilitiesPanelProps {
  tableId: string
  characters: PanelCharacter[]
  currentUserId: string
  isMaster: boolean
  subscribe: (fn: (data: any) => void) => () => void
}

export function AbilitiesPanel({ tableId, characters, currentUserId, isMaster, subscribe }: AbilitiesPanelProps) {
  const [catalog, setCatalog] = useState<GameAbility[]>([])
  const [abilities, setAbilities] = useState<AbilityEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [granting, setGranting] = useState<string | null>(null)

  // Personagem em foco: para o mestre é o selecionado; para o jogador é o próprio.
  const ownCharacter = characters.find((c) => c.userId === currentUserId && !c.isNpc)
  const [selectedCharId, setSelectedCharId] = useState<string>('')

  const viewedCharId = isMaster ? selectedCharId : (ownCharacter?.id ?? '')
  const viewedCharIdRef = useRef(viewedCharId)
  viewedCharIdRef.current = viewedCharId

  // Mestre: escolher um personagem por padrão (o primeiro)
  useEffect(() => {
    if (isMaster && !selectedCharId && characters.length > 0) {
      setSelectedCharId(characters[0].id)
    }
  }, [isMaster, characters, selectedCharId])

  // Catálogo (só o mestre precisa dele para conceder)
  useEffect(() => {
    if (!isMaster || !tableId) return
    let cancelled = false
    apiService.getGameConfig(tableId)
      .then((cfg) => { if (!cancelled) setCatalog(cfg?.abilities ?? []) })
      .catch(() => { if (!cancelled) setCatalog([]) })
    return () => { cancelled = true }
  }, [isMaster, tableId])

  // Habilidades do personagem em foco
  useEffect(() => {
    if (!viewedCharId) { setAbilities([]); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    apiService.getCharacterAbilities(viewedCharId)
      .then((data) => { if (!cancelled) setAbilities(data as AbilityEntry[]) })
      .catch(() => { if (!cancelled) toast.error('Erro ao carregar habilidades') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [viewedCharId])

  // Tempo real: aplica abilities_update quando é do personagem em foco
  useEffect(() => {
    const handler = (data: any) => {
      if (data?.type !== 'abilities_update') return
      if (data.characterId !== viewedCharIdRef.current) return
      const e = data.entry as AbilityEntry
      setAbilities((prev) => {
        if (e.removed) return prev.filter((x) => x.abilityId !== e.abilityId)
        const idx = prev.findIndex((x) => x.abilityId === e.abilityId)
        if (idx >= 0) {
          const cp = [...prev]
          cp[idx] = { ...cp[idx], ...e }
          return cp
        }
        return [e, ...prev]
      })
    }
    const unsubscribe = subscribe(handler)
    return unsubscribe
  }, [subscribe])

  const handleGrant = useCallback(async (ability: GameAbility) => {
    if (!viewedCharId) return
    setGranting(ability.id)
    try {
      await apiService.grantAbility(viewedCharId, ability.id)
      // O broadcast atualiza a lista
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao conceder habilidade')
    } finally {
      setGranting(null)
    }
  }, [viewedCharId])

  const handleRevoke = useCallback(async (entry: AbilityEntry) => {
    if (!viewedCharId) return
    try {
      await apiService.revokeAbility(viewedCharId, entry.abilityId)
      setAbilities((prev) => prev.filter((x) => x.abilityId !== entry.abilityId))
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao remover habilidade')
    }
  }, [viewedCharId])

  const viewedName = characters.find((c) => c.id === viewedCharId)?.name

  // Habilidades que o personagem em foco ainda nao sabe (para o catalogo)
  const knownIds = new Set(abilities.map((a) => a.abilityId))

  // ---- Jogador sem personagem ----
  if (!isMaster && !ownCharacter) {
    return (
      <div className="h-full bg-game-secondary dark:bg-zinc-900/95 flex flex-col items-center justify-center text-center p-6">
        <div className="w-12 h-12 bg-game-tertiary dark:bg-zinc-800/50 rounded-full flex items-center justify-center mb-3">
          <Sparkles className="h-6 w-6 text-game-muted" />
        </div>
        <p className="text-sm text-game-muted">Você ainda não tem personagem nesta mesa.</p>
        <p className="text-xs text-game-muted mt-1">Suas habilidades aparecerão aqui quando tiver um.</p>
      </div>
    )
  }

  return (
    <div className="h-full bg-game-secondary dark:bg-zinc-900/95 flex flex-col">
      <div className="p-4 border-b border-game-border-primary dark:border-zinc-800">
        <p className="text-xs uppercase tracking-[0.2em] text-game-muted dark:text-zinc-500 mb-1">Habilidades</p>
        {isMaster ? (
          <>
            <h3 className="font-semibold text-lg text-game-primary dark:text-zinc-100">Habilidades dos personagens</h3>
            <select
              value={selectedCharId}
              onChange={(e) => setSelectedCharId(e.target.value)}
              className="mt-2 flex h-9 w-full rounded-md border border-game-border-primary dark:border-zinc-700 bg-white dark:bg-zinc-800/80 px-3 py-1 text-sm text-game-primary dark:text-zinc-100"
            >
              {characters.length === 0 && <option value="">Nenhum personagem</option>}
              {characters.map((c) => (
                <option key={c.id} value={c.id}>{c.isNpc ? `${c.name} (NPC)` : c.name}</option>
              ))}
            </select>
          </>
        ) : (
          <h3 className="font-semibold text-lg text-game-primary dark:text-zinc-100">
            Habilidades de {ownCharacter?.name}
          </h3>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
            </div>
          )}

          {/* Habilidades do personagem em foco */}
          {!loading && (
            <div className="space-y-2">
              {abilities.length === 0 ? (
                <div className="text-center py-8 px-4 text-game-muted">
                  <div className="w-12 h-12 mx-auto bg-game-tertiary dark:bg-zinc-800/50 rounded-full flex items-center justify-center mb-3">
                    <Sparkles className="h-6 w-6 text-game-muted" />
                  </div>
                  <p className="text-sm">Nenhuma habilidade ainda</p>
                  {isMaster && <p className="text-xs mt-1">Conceda habilidades do catálogo abaixo.</p>}
                </div>
              ) : (
                <div className="space-y-2">
                  {abilities.map((entry) => (
                    <div
                      key={entry.abilityId}
                      className="group rounded-xl border border-game-border-primary dark:border-zinc-800 bg-white dark:bg-zinc-800/40 p-3 flex items-start gap-3"
                    >
                      <div className="text-2xl leading-none">{entry.icon || '✨'}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-game-primary dark:text-zinc-100">{entry.name}</div>
                        {entry.description && (
                          <div className="text-xs text-game-muted mt-0.5">{entry.description}</div>
                        )}
                      </div>
                      {isMaster && (
                        <button
                          onClick={() => handleRevoke(entry)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-game-muted hover:text-rose-400 shrink-0"
                          title="Remover do personagem"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Catálogo (só o mestre): conceder ao personagem em foco */}
          {isMaster && (
            <div className="pt-2 border-t border-game-border-primary dark:border-zinc-800">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="h-4 w-4 text-violet-500" />
                <h4 className="text-sm font-semibold text-game-primary dark:text-zinc-100">Catálogo</h4>
              </div>
              {catalog.length === 0 ? (
                <p className="text-xs text-game-muted py-2">
                  Nenhuma habilidade no catálogo. Crie habilidades em "Habilidades da Campanha" na página da mesa.
                </p>
              ) : (
                <div className="space-y-2">
                  {catalog.map((ability) => (
                    <div
                      key={ability.id}
                      className="flex items-center gap-3 rounded-xl border border-game-border-primary dark:border-zinc-800 bg-white dark:bg-zinc-800/40 p-2.5"
                    >
                      <div className="text-2xl">{ability.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-game-primary dark:text-zinc-100 truncate">{ability.name}</div>
                        {ability.description && (
                          <div className="text-[11px] text-game-muted truncate">{ability.description}</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleGrant(ability)}
                        disabled={!viewedCharId || granting === ability.id || knownIds.has(ability.id)}
                        className="gap-1 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white"
                        title={
                          knownIds.has(ability.id)
                            ? 'O personagem já sabe esta habilidade'
                            : viewedName ? `Conceder a ${viewedName}` : 'Selecione um personagem'
                        }
                      >
                        {granting === ability.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        Conceder
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
